import React, { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
    FileCheck, AlertTriangle, CheckCircle2, Clock,
    Download, Plus, Search, Calendar, FileText, X,
} from 'lucide-react';
import { MODULE_ICONS } from '../../../components/icons/ModuleIcons';
import { useSiteDisplayName } from '../../../utils/siteUtils';
import '../../../styles/np-theme.css';

interface ComplianceItem {
    id: string;
    category: string;
    requirement: string;
    dueDate: string;
    status: 'compliant' | 'pending' | 'overdue' | 'not-applicable';
    lastUpdated: string;
    responsible: string;
    document?: string;
}

// Illustrative rows only: the compliance module has no backend yet. The page labels them as
// sample data so nobody mistakes them for the plant's real compliance position.
const SAMPLE_COMPLIANCE: ComplianceItem[] = [
    {
        id: '1',
        category: 'Environmental',
        requirement: 'Environmental Clearance Certificate',
        dueDate: '2026-12-31',
        status: 'compliant',
        lastUpdated: '2026-01-10',
        responsible: 'Site Manager',
        document: 'EC_Certificate_2026.pdf',
    },
    {
        id: '2',
        category: 'Safety',
        requirement: 'Monthly Safety Inspection Report',
        dueDate: '2026-03-31',
        status: 'pending',
        lastUpdated: '2026-02-28',
        responsible: 'Safety Officer',
    },
    {
        id: '3',
        category: 'Electrical',
        requirement: 'Electrical Installation Certificate (CEI)',
        dueDate: '2026-06-30',
        status: 'compliant',
        lastUpdated: '2026-01-20',
        responsible: 'Electrical Engineer',
        document: 'CEI_2026.pdf',
    },
    {
        id: '4',
        category: 'Statutory',
        requirement: 'Annual Performance Report to DISCOM',
        dueDate: '2026-03-15',
        status: 'overdue',
        lastUpdated: '2025-03-01',
        responsible: 'O&M Manager',
    },
    {
        id: '5',
        category: 'Grid',
        requirement: 'Power Purchase Agreement Compliance Review',
        dueDate: '2026-12-31',
        status: 'compliant',
        lastUpdated: '2026-02-01',
        responsible: 'Contract Manager',
        document: 'PPA_Compliance_2026.pdf',
    },
    {
        id: '6',
        category: 'Safety',
        requirement: 'Fire Safety Audit',
        dueDate: '2026-04-30',
        status: 'pending',
        lastUpdated: '2025-04-30',
        responsible: 'Safety Officer',
    },
];

const STATUS_CONFIG = {
    'compliant': { label: 'Compliant', color: 'bg-green-50 text-green-700 border-green-200', icon: CheckCircle2, iconColor: 'text-green-600' },
    'pending': { label: 'Pending', color: 'bg-blue-50 text-blue-700 border-blue-200', icon: Clock, iconColor: 'text-blue-600' },
    'overdue': { label: 'Overdue', color: 'bg-red-50 text-red-700 border-red-200', icon: AlertTriangle, iconColor: 'text-red-600' },
    'not-applicable': { label: 'N/A', color: 'bg-slate-50 text-slate-600 border-slate-200', icon: FileCheck, iconColor: 'text-slate-400' },
};

const formatDate = (iso: string) => {
    const [y, m, d] = iso.split('-').map(Number);
    return new Date(y, m - 1, d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const NOT_CONNECTED = 'Available once the compliance module is connected';

const CompliancePage: React.FC = () => {
    const { site } = useParams<{ site: string }>();
    const siteDisplayName = useSiteDisplayName(site);
    const [searchQuery, setSearchQuery] = useState('');
    const items = SAMPLE_COMPLIANCE;
    const ComplianceIcon = MODULE_ICONS.compliance;

    const stats = {
        total: items.length,
        compliant: items.filter(i => i.status === 'compliant').length,
        pending: items.filter(i => i.status === 'pending').length,
        overdue: items.filter(i => i.status === 'overdue').length,
    };
    const compliancePercent = Math.round((stats.compliant / stats.total) * 100);

    const filtered = useMemo(() => {
        const q = searchQuery.trim().toLowerCase();
        if (!q) return items;
        return items.filter(i =>
            i.requirement.toLowerCase().includes(q) ||
            i.category.toLowerCase().includes(q) ||
            i.responsible.toLowerCase().includes(q)
        );
    }, [items, searchQuery]);

    return (
        <div className="np-page min-h-screen">
            <div className="np-theme max-w-7xl mx-auto px-4 sm:px-6 pt-24 pb-12">
                {/* Page header */}
                <div className="flex flex-wrap items-center justify-between gap-3 pb-5 border-b border-slate-200">
                    <div className="np-pagehead">
                        <div className="np-pagehead__mark">
                            <ComplianceIcon size={20} />
                        </div>
                        <div style={{ minWidth: 0 }}>
                            <h1 className="np-pagehead__title">Compliance</h1>
                            <div className="np-pagehead__meta">
                                <span className="np-pagehead__site">{siteDisplayName || site || 'SITE'}</span>
                                <span className="np-pagehead__desc">Statutory, safety and contractual obligations</span>
                            </div>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            type="button"
                            disabled
                            title={NOT_CONNECTED}
                            className="inline-flex items-center gap-2 h-9 px-3 rounded border border-slate-200 bg-white text-sm text-slate-400 cursor-not-allowed"
                        >
                            <Download size={15} aria-hidden="true" /> Export
                        </button>
                        <button
                            type="button"
                            disabled
                            title={NOT_CONNECTED}
                            className="inline-flex items-center gap-2 h-9 px-3 rounded border border-slate-200 bg-slate-100 text-sm text-slate-400 cursor-not-allowed"
                        >
                            <Plus size={15} aria-hidden="true" /> Add Item
                        </button>
                    </div>
                </div>

                <div className="np-note mt-5" role="status">
                    <AlertTriangle size={15} className="flex-shrink-0 mt-0.5" aria-hidden="true" />
                    <p className="text-sm">
                        <b>Sample data.</b> The compliance tracker is not connected to your plant’s records yet. The
                        requirements below are examples of how it will look; adding items and exporting will be enabled
                        when it goes live.
                    </p>
                </div>

                {/* Summary */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-5">
                    <div className="np-kpi">
                        <div className="np-kpi__head">
                            <span className="np-kpi__label">Overall compliance</span>
                        </div>
                        <span className="np-kpi__value">{compliancePercent}%</span>
                        <div
                            className="mt-2 h-1.5 w-full rounded bg-slate-200 overflow-hidden"
                            role="progressbar"
                            aria-valuenow={compliancePercent}
                            aria-valuemin={0}
                            aria-valuemax={100}
                            aria-label="Overall compliance"
                        >
                            <div className="h-full bg-blue-600" style={{ width: `${compliancePercent}%` }} />
                        </div>
                        <span className="np-kpi__hint">{stats.compliant} of {stats.total} requirements met</span>
                    </div>
                    {[
                        { label: 'Compliant', value: stats.compliant, icon: CheckCircle2, tone: 'var(--np-ok)' },
                        { label: 'Pending', value: stats.pending, icon: Clock, tone: 'var(--np-blue-2)' },
                        { label: 'Overdue', value: stats.overdue, icon: AlertTriangle, tone: 'var(--np-bad)' },
                    ].map(s => (
                        <div key={s.label} className="np-kpi">
                            <div className="np-kpi__head">
                                <span className="np-kpi__label">{s.label}</span>
                                <span className="np-kpi__mark"><s.icon size={14} aria-hidden="true" /></span>
                            </div>
                            <span className="np-kpi__value" style={{ color: s.tone }}>{s.value}</span>
                            <span className="np-kpi__hint">requirements</span>
                        </div>
                    ))}
                </div>

                {/* Requirements */}
                <section className="mt-5 bg-white border border-slate-200 rounded" aria-labelledby="compliance-list-title">
                    <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-b border-slate-100">
                        <h2 id="compliance-list-title" className="text-base">Requirements</h2>
                        <div className="relative w-full sm:w-72">
                            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" aria-hidden="true" />
                            <input
                                type="text"
                                placeholder="Search requirement, category or owner"
                                aria-label="Search compliance requirements"
                                value={searchQuery}
                                onChange={e => setSearchQuery(e.target.value)}
                                className="w-full h-9 pl-8 pr-8 rounded border border-slate-300 bg-white text-sm outline-none focus:ring-2 focus:ring-blue-200"
                            />
                            {searchQuery && (
                                <button
                                    type="button"
                                    onClick={() => setSearchQuery('')}
                                    aria-label="Clear search"
                                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded text-slate-400 hover:bg-slate-100 hover:text-slate-700"
                                >
                                    <X size={13} />
                                </button>
                            )}
                        </div>
                    </div>

                    {filtered.length === 0 ? (
                        <div className="px-4 py-12 text-center">
                            <p className="text-sm text-slate-900">No requirements match “{searchQuery}”</p>
                            <button
                                type="button"
                                onClick={() => setSearchQuery('')}
                                className="mt-2 text-sm text-blue-700 hover:underline"
                            >
                                Clear search
                            </button>
                        </div>
                    ) : (
                        <ul className="divide-y divide-slate-100">
                            {filtered.map(item => {
                                const cfg = STATUS_CONFIG[item.status];
                                return (
                                    <li key={item.id} className="flex items-start gap-4 px-4 py-3.5">
                                        <cfg.icon size={18} className={`${cfg.iconColor} flex-shrink-0 mt-0.5`} aria-hidden="true" />
                                        <div className="flex-1 min-w-0">
                                            <div className="flex flex-wrap items-center gap-2">
                                                <span className="text-[11px] text-slate-500 uppercase">{item.category}</span>
                                                <span className={`text-[11px] px-2 py-0.5 rounded border ${cfg.color}`}>{cfg.label}</span>
                                            </div>
                                            <h3 className="mt-1 text-sm text-slate-900">{item.requirement}</h3>
                                            <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500">
                                                <span className="inline-flex items-center gap-1">
                                                    <Calendar size={11} aria-hidden="true" /> Due {formatDate(item.dueDate)}
                                                </span>
                                                <span>Owner: {item.responsible}</span>
                                                <span>Updated {formatDate(item.lastUpdated)}</span>
                                            </div>
                                        </div>
                                        {item.document && (
                                            <span className="hidden sm:inline-flex items-center gap-1.5 text-xs text-slate-500" title={NOT_CONNECTED}>
                                                <FileText size={13} aria-hidden="true" /> {item.document}
                                            </span>
                                        )}
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </section>
            </div>
        </div>
    );
};

export default CompliancePage;
